import { Coverage } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { InsuranceVerificationDetailsResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { InsuranceReportApiTypes } from '@frontend/api-insurance-eligibility-report';
import { formatDate } from '@frontend/date';
import { createInsuranceDetailsMismatchMap } from '@frontend/insurance-eligibility-report-tuuthfairy';
import { UndefinedOrNull } from '../utils/types';
import { useGetLocationVerticalForPayerList } from './use-get-location-vertical-for-payer-list';
import { useGetVerifiedInsuranceDetailsShallowStore } from './use-get-verified-insurance-details-store';

type UseGetVerifiedInsuranceDetailsListProps = {
  insuranceDetails: UndefinedOrNull<InsuranceVerificationDetailsResponse>;
  locationId: string;
};

type UseGetVerifiedInsuranceDetailsListReturn = {
  payerId: string;
  payerName: string;
  groupNumber: string;
  groupName: string;
  verificationDate: string;
  effectiveDate: string;
  planType: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  relationship: string;
  nationalProviderId: string;
  providerTaxId: string;
  memberId: string;
  subscriberFirstName?: string;
  subscriberLastName?: string;
  subscriberDateOfBirth?: string;
  subscriberMemberId?: string;
  isLoading?: boolean;
  status?: string;
  pmsPayerId?: string;
  endDate?: string;
  mismatchedMemberId?: string;
};

const getDentalVerifiedInsuranceDetails = (
  dentalInsuranceCoverageDetails?: Coverage
): UseGetVerifiedInsuranceDetailsListReturn => {
  const { thirdPartyCoverage: dentalInsuranceDetails } = dentalInsuranceCoverageDetails || {};
  const misMatchDetails = createInsuranceDetailsMismatchMap(dentalInsuranceDetails?.patientMismatchDetails || []);

  const status = dentalInsuranceDetails?.coverage?.status || '';
  return {
    payerId:
      dentalInsuranceDetails?.insuranceRequestDetails?.payerId || dentalInsuranceDetails?.details?.plan?.number || '',
    payerName:
      dentalInsuranceDetails?.insuranceRequestDetails?.payerName || dentalInsuranceDetails?.details?.plan?.name || '',
    verificationDate: dentalInsuranceDetails?.lastVerifiedAt
      ? dayjs(dentalInsuranceDetails?.lastVerifiedAt).format('DD MMMM YYYY, h:mm A')
      : '-',
    groupNumber:
      dentalInsuranceDetails?.insuranceRequestDetails?.groupNumber ||
      dentalInsuranceDetails?.details?.plan?.group_number ||
      '',
    effectiveDate: dentalInsuranceDetails?.coverage?.effectiveDate || '',
    planType:
      dentalInsuranceDetails?.dentalSummaryInfo?.plan?.type || dentalInsuranceDetails?.details?.plan?.type || '',
    firstName:
      dentalInsuranceDetails?.insuranceRequestDetails?.firstName ||
      dentalInsuranceDetails?.dentalSummaryInfo?.patient?.firstName ||
      dentalInsuranceDetails?.details?.subscriber?.first_name ||
      '',
    lastName:
      dentalInsuranceDetails?.insuranceRequestDetails?.lastName ||
      dentalInsuranceDetails?.dentalSummaryInfo?.patient?.lastName ||
      dentalInsuranceDetails?.details?.subscriber?.last_name ||
      '',
    birthDate: formatDate(
      dentalInsuranceDetails?.insuranceRequestDetails?.birthDate ||
        dentalInsuranceDetails?.dentalSummaryInfo?.patient?.dateOfBirth ||
        dentalInsuranceDetails?.details?.subscriber?.dob,
      'MMMM D, YYYY'
    ),
    relationship:
      dentalInsuranceDetails?.insuranceRequestDetails?.relationship ||
      dentalInsuranceDetails?.dentalSummaryInfo?.patient?.relationship ||
      dentalInsuranceDetails?.details?.subscriber?.relationship ||
      '',
    subscriberDateOfBirth:
      dentalInsuranceDetails?.insuranceRequestDetails?.subscriberDateOfBirth ||
      dentalInsuranceDetails?.dentalSummaryInfo?.subscriber?.dateOfBirth ||
      dentalInsuranceDetails?.details?.subscriber?.dob ||
      '',
    subscriberFirstName:
      dentalInsuranceDetails?.insuranceRequestDetails?.subscriberFirstName ||
      dentalInsuranceDetails?.dentalSummaryInfo?.subscriber?.firstName ||
      dentalInsuranceDetails?.details?.subscriber?.first_name ||
      '',
    subscriberLastName:
      dentalInsuranceDetails?.insuranceRequestDetails?.subscriberLastName ||
      dentalInsuranceDetails?.dentalSummaryInfo?.subscriber?.lastName ||
      dentalInsuranceDetails?.details?.subscriber?.last_name ||
      '',
    subscriberMemberId: dentalInsuranceDetails?.insuranceRequestDetails?.subscriberMemberId || '',
    nationalProviderId: dentalInsuranceDetails?.insuranceRequestDetails?.nationalProviderId || '',
    providerTaxId: dentalInsuranceDetails?.insuranceRequestDetails?.providerTaxId || '',
    memberId: dentalInsuranceDetails?.insuranceRequestDetails?.memberId || '',
    groupName: dentalInsuranceDetails?.insuranceRequestDetails?.groupName || '',
    status,
    pmsPayerId: dentalInsuranceDetails?.insuranceRequestDetails?.pmsPayerId || '',
    endDate: dentalInsuranceDetails?.coverage?.endDate || '',
    mismatchedMemberId: misMatchDetails?.['PATIENT_MEMBER_ID']?.planValue || '',
  };
};

const getNonDentalVerifiedInsuranceDetails = (nonDentalInsuranceDetails?: InsuranceVerificationDetailsResponse) => {
  const status = nonDentalInsuranceDetails?.coverage?.thirdPartyCoverage?.coverage?.status;
  const nonDentalInsuranceDetailsData =
    nonDentalInsuranceDetails?.coverage?.thirdPartyCoverage?.insuranceRequestDetails;
  const lastVerifiedAt = nonDentalInsuranceDetails?.coverage?.thirdPartyCoverage?.lastVerifiedAt || '';
  const effectiveDate = nonDentalInsuranceDetails?.coverage?.thirdPartyCoverage?.coverage?.effectiveDate || '';
  return {
    payerId: nonDentalInsuranceDetailsData?.payerId || '',
    payerName: nonDentalInsuranceDetailsData?.payerName || '',
    verificationDate: lastVerifiedAt ? dayjs(lastVerifiedAt).format('DD MMMM YYYY, h:mm A') : '-',
    groupNumber: nonDentalInsuranceDetailsData?.groupNumber || '',
    effectiveDate: effectiveDate || '',
    planType: '',
    firstName: nonDentalInsuranceDetailsData?.firstName || '',
    lastName: nonDentalInsuranceDetailsData?.lastName || '',
    birthDate: nonDentalInsuranceDetailsData?.birthDate
      ? formatDate(nonDentalInsuranceDetailsData?.birthDate, 'MMMM D, YYYY')
      : '',
    relationship: nonDentalInsuranceDetailsData?.relationship || '',
    subscriberDateOfBirth: nonDentalInsuranceDetailsData?.subscriberDateOfBirth || '',
    subscriberFirstName: nonDentalInsuranceDetailsData?.subscriberFirstName || '',
    subscriberLastName: nonDentalInsuranceDetailsData?.subscriberLastName || '',
    subscriberMemberId: nonDentalInsuranceDetailsData?.subscriberMemberId || '',
    nationalProviderId: nonDentalInsuranceDetailsData?.nationalProviderId || '',
    providerTaxId: nonDentalInsuranceDetailsData?.providerTaxId || '',
    memberId: nonDentalInsuranceDetailsData?.memberId || '',
    groupName: nonDentalInsuranceDetailsData?.groupName || '',
    status,
  };
};

export const useGetVerifiedInsuranceDetailsList = ({
  insuranceDetails,
  locationId,
}: UseGetVerifiedInsuranceDetailsListProps): UseGetVerifiedInsuranceDetailsListReturn => {
  const { isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(locationId);
  const { setInsuranceRequestDetails } = useGetVerifiedInsuranceDetailsShallowStore('setInsuranceRequestDetails');

  setInsuranceRequestDetails({
    ...insuranceDetails?.coverage?.thirdPartyCoverage?.insuranceRequestDetails,
  } as InsuranceReportApiTypes.InsuranceReportRequestMetaDataType);

  if (isDental && !isEmpty(insuranceDetails)) {
    return getDentalVerifiedInsuranceDetails(insuranceDetails?.coverage as Coverage);
  } else if (isWellnessVertical && !isEmpty(insuranceDetails)) {
    return getNonDentalVerifiedInsuranceDetails(insuranceDetails as InsuranceVerificationDetailsResponse);
  } else {
    return {} as UseGetVerifiedInsuranceDetailsListReturn;
  }
};
